import { ModalPostsStyled } from "./styles";
import { Modal } from "../../../../../components/Modal";
import {
	Dispatch,
	SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { PostContext } from "../../../../../providers/PostContext";
import { Posts } from "../../../../../components/Posts";
import { Loading } from "../../../../../components/LoadingProps/loading";

type iUsersWithoutPass = {
	id: number;
	createdAt: Date;
	updatedAt: Date;
	name: string;
	username: string;
	icon: string;
	role: "user" | "admin";
	phone: string;
	email: string;
	deletedAt: Date | null;
	bio?: string;
	image?: string;
};

type iUser = {
	id: number;
	createdAt: Date;
	updatedAt: Date;
	name: string;
	username: string;
	icon: string;
	role: "user" | "admin";
	phone: string;
	email: string;
	password: string;
	deletedAt: Date | null;
	bio?: string;
	image?: string;
};

type iPostUserImageLikeSchema = {
	count: number;
	raw: {
		id: number;
		message: string;
		like: number;
		dislike: number;
		comment: number;
		Images: {
			id: number;
			createdAt: Date;
			publicId: string;
			url: string;
			secureUrl: string;
		}[];
		User: iUser;
		createdAt: Date;
		updatedAt: Date;
	}[];
};

interface iModalPosts {
	posts: iPostUserImageLikeSchema | null;
	showPosts: boolean;
	setShowPosts: Dispatch<SetStateAction<boolean>>;
	postIdClicked?: number;
	page: number;
	setPage: Dispatch<SetStateAction<number>>;
	hasMorePosts: boolean;
	setHasMorePosts: Dispatch<SetStateAction<boolean>>;
}

export const ModalPosts = ({
	posts,
	showPosts,
	setShowPosts,
	postIdClicked,
	page,
	setPage,
	hasMorePosts,
	setHasMorePosts,
}: iModalPosts) => {
	const { getUserPosts } = useContext(PostContext);

	const [loading, setLoading] = useState(false);

	const sentinelaRef = useRef<HTMLLIElement | null>(null); //referência ao elemento sentinela

	const handleLoadPosts = useCallback(async () => {
		// O useCallback: carrega os posts e atualiza o estado de carregamento
		setLoading(true);

		if (posts?.raw[0]) {
			const userId = Number(posts?.raw[0].User.id); // id do dono do perfil

			const response = await getUserPosts(userId, page);
			if (response && response.count <= 20 * page) {
				setHasMorePosts(false);
			}
		}

		setLoading(false);
	}, [page]);

	useEffect(() => {
		handleLoadPosts();
	}, [handleLoadPosts]);

	useEffect(() => {
		// observar o elemento sentinela
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					setPage((prevPage) => prevPage + 1);
				}
			},
			{
				root: null,
				rootMargin: "0px",
				threshold: 1.0, // garante que o elemento sentinela precisa estar totalmente visível para carregar mais posts
			}
		);

		if (sentinelaRef.current) {
			observer.observe(sentinelaRef.current);
		}

		if (!hasMorePosts) {
			if (sentinelaRef.current) {
				observer.unobserve(sentinelaRef.current);
			}
		}

		return () => {
			if (sentinelaRef.current) {
				observer.unobserve(sentinelaRef.current);
			}
		};
	}, [showPosts]);

	return (
		<ModalPostsStyled>
			{showPosts && (
				<Modal showModal={setShowPosts}>
					<div className="cards">
						<Posts posts={posts} postIdClicked={postIdClicked} />
						<li ref={sentinelaRef} id="sentinela" />
						{loading && <Loading $width="50px" />}
					</div>
				</Modal>
			)}
		</ModalPostsStyled>
	);
};
